import { useState } from "react";

import { Modal, Span, Tabs, type TabsItem } from "@virtual-brain-lib/frontend-design-system/ui";
import type { MessageToolDocument } from "@virtual-brain-lib/types";

import { DocumentRow } from "./DocumentRow";
import { DocumentSectionHeader } from "./DocumentSectionHeader";
import { groupDocumentsBySourceLabel } from "./groupDocumentsBySourceLabel";

type ViewMode = "default" | "grouped";

interface ToolCardAllDocumentsModalProps {
	readonly isOpen: boolean;
	readonly close: () => void;
	readonly documents: readonly MessageToolDocument[];
	readonly contentReferenceOffset?: number;
	readonly onDocumentClick?: (doc: MessageToolDocument) => void;
}

export function ToolCardAllDocumentsModal({
	isOpen,
	close,
	documents,
	contentReferenceOffset,
	onDocumentClick,
}: ToolCardAllDocumentsModalProps) {
	const [viewMode, setViewMode] = useState<ViewMode>("default");
	const hasAnySourceLabel = documents.some((doc) => Boolean(doc.sourceLabel));

	const tabs: Array<TabsItem<unknown, ViewMode>> = [
		{ key: "default", title: "Relevance" },
		{ key: "grouped", title: "Source" },
	];

	const effectiveViewMode = hasAnySourceLabel ? viewMode : "default";

	return (
		<Modal
			close={close}
			icon="file"
			isOpen={isOpen}
			size="lg"
			classNames={{
				body: "p-0",
			}}
			footer={{
				secondaryButton: {
					hidden: true,
				},
			}}
			title={`All documents (${documents.length})`}
		>
			<div className="flex flex-col gap-2">
				{hasAnySourceLabel && (
					<Tabs
						selectedKey={viewMode}
						size="sm"
						tabs={tabs}
						variant="underline"
						onSelectedTabChange={(key) => {
							setViewMode(key);
						}}
					/>
				)}

				<div className="max-h-[80vh] overflow-y-auto px-4 pt-4">
					{effectiveViewMode === "default" ? (
						<DefaultView
							documents={documents}
							contentReferenceOffset={contentReferenceOffset}
							onDocumentClick={onDocumentClick}
						/>
					) : (
						<GroupedView
							documents={documents}
							contentReferenceOffset={contentReferenceOffset}
							onDocumentClick={onDocumentClick}
						/>
					)}
				</div>
			</div>
		</Modal>
	);
}

function DefaultView({
	documents,
	contentReferenceOffset,
	onDocumentClick,
}: {
	readonly documents: readonly MessageToolDocument[];
	readonly contentReferenceOffset?: number;
	readonly onDocumentClick?: (doc: MessageToolDocument) => void;
}) {
	const offset = contentReferenceOffset ?? 0;
	const reviewedDocuments = offset > 0 ? documents.slice(0, offset) : [];
	const availableDocuments = offset > 0 ? documents.slice(offset) : documents;

	const handleDocumentClick = (doc: MessageToolDocument) => {
		if (doc.url) {
			window.open(doc.url, "_blank");
		} else {
			onDocumentClick?.(doc);
		}
	};

	if (offset === 0 || reviewedDocuments.length === 0) {
		return (
			<div className="flex flex-col">
				{documents.map((doc) => (
					<DocumentRow
						key={doc.id}
						document={doc}
						onDocumentClick={handleDocumentClick}
					/>
				))}
			</div>
		);
	}

	return (
		<div className="mb-4 flex flex-col gap-6">
			{reviewedDocuments.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-0.5">
						<Span isBlock className="font-semibold" variant="body-sm">
							Reviewed ({reviewedDocuments.length})
						</Span>
						<Span isBlock color="defaultLight" variant="body-xs">
							Taken into account when generating this answer.
						</Span>
					</div>
					<div className="border-subtle h-60 overflow-y-auto rounded-lg border bg-background p-2">
						{reviewedDocuments.map((doc) => (
							<DocumentRow
								key={doc.id}
								document={doc}
								onDocumentClick={handleDocumentClick}
							/>
						))}
					</div>
				</div>
			)}

			{availableDocuments.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-0.5">
						<Span isBlock className="font-semibold" variant="body-sm">
							Available for deeper analysis ({availableDocuments.length})
						</Span>
						<Span isBlock color="defaultLight" variant="body-xs">
							Click 'Dig Deeper' in your deliverable to take these into account.
						</Span>
					</div>
					<div className="border-subtle h-60 overflow-y-auto rounded-lg border bg-secondary/20 p-2">
						{availableDocuments.map((doc) => (
							<DocumentRow
								key={doc.id}
								document={doc}
								onDocumentClick={handleDocumentClick}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function GroupedView({
	documents,
	contentReferenceOffset,
	onDocumentClick,
}: {
	readonly documents: readonly MessageToolDocument[];
	readonly contentReferenceOffset?: number;
	readonly onDocumentClick?: (doc: MessageToolDocument) => void;
}) {
	const offset = contentReferenceOffset ?? 0;
	const reviewedDocuments = offset > 0 ? documents.slice(0, offset) : [];
	const availableDocuments = offset > 0 ? documents.slice(offset) : documents;

	const reviewedGroups =
		reviewedDocuments.length > 0 ? groupDocumentsBySourceLabel(reviewedDocuments) : [];
	const availableGroups =
		availableDocuments.length > 0 ? groupDocumentsBySourceLabel(availableDocuments) : [];

	if (offset === 0 || reviewedDocuments.length === 0) {
		const groups = groupDocumentsBySourceLabel(documents);

		return (
			<div className="flex flex-col gap-3">
				{groups.map((group) => (
					<div key={group.key} className="flex flex-col gap-1.5">
						{group.label && <DocumentSectionHeader label={group.label} />}
						<div className="flex flex-col">
							{group.documents.map((doc) => (
								<DocumentRow
									key={doc.id}
									document={doc}
									onDocumentClick={onDocumentClick}
								/>
							))}
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="mb-4 flex flex-col gap-6">
			{reviewedGroups.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-0.5">
						<Span isBlock className="font-semibold" variant="body-sm">
							Reviewed ({reviewedDocuments.length})
						</Span>
						<Span isBlock color="defaultLight" variant="body-xs">
							Taken into account when generating this answer.
						</Span>
					</div>
					<div className="border-subtle h-60 overflow-y-auto rounded-lg border bg-background p-2">
						<div className="flex flex-col gap-3">
							{reviewedGroups.map((group) => (
								<div key={group.key} className="flex flex-col gap-1.5">
									{group.label && <DocumentSectionHeader label={group.label} />}
									<div className="flex flex-col">
										{group.documents.map((doc) => (
											<DocumentRow
												key={doc.id}
												document={doc}
												onDocumentClick={onDocumentClick}
											/>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{availableGroups.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-0.5">
						<Span isBlock className="font-semibold" variant="body-sm">
							Available for deeper analysis ({availableDocuments.length})
						</Span>
						<Span isBlock color="defaultLight" variant="body-xs">
							Click 'Dig Deeper' in your deliverable to take these into account.
						</Span>
					</div>
					<div className="border-subtle h-60 overflow-y-auto rounded-lg border bg-secondary/20 p-2">
						<div className="flex flex-col gap-3">
							{availableGroups.map((group) => (
								<div key={group.key} className="flex flex-col gap-1.5">
									{group.label && <DocumentSectionHeader label={group.label} />}
									<div className="flex flex-col">
										{group.documents.map((doc) => (
											<DocumentRow
												key={doc.id}
												document={doc}
												onDocumentClick={onDocumentClick}
											/>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
